import React, { useContext, useState } from "react";
import { Box, Slide } from "@mui/material";
import { InputField, SIZE } from "@metacrm/metacrm-material-ui/dist/InputField";
import { useIntl } from "react-intl";
import NotificationReportContext from "features/metadesk/Notification/NotificationReport/NotificationReport.context";
import Grid from "@metacrm/metacrm-material-ui/dist/Grid";

import PopperMenu from "@metacrm/metacrm-material-ui/dist/PopperMenu";
import useNavigateEntity from "hooks/useNavigateEntity";
import CRMTable from "@metacrm/metacrm-material-ui/dist/Table";
import {
  StyledTableHeader,
  StyledDetailHint,
} from "./NotificationReportContent.styles";
import { map } from "lodash-es";

import DateArea from "features/metadesk/CommunityAnalysis/components/DateArea";
import ChannelPerformance from "features/metadesk/Notification/NotificationReport/component/ChannelPerformance/ChannelPerformance";
import WhiteCard from "features/metadesk/importList/components/WhiteCard";

const NotificationReportContent = () => {
  const { formatMessage } = useIntl();
  const {
    searchText,
    selectedItems,
    handleSetSelectedItems,
    handleClearSelectedItems,
    notificationList,
    channelPerformance: channelPerformanceData,
    page,
    handleChangePage,
    totalItems,
    handleSearchChange,
    handleCategoryChange,
    renderContents,
    order,
    orderBy,
    handleRequestSort,
    actionsList,
    loading,
    anchorEl,
    handleCloseActions,
    isFeatureLocked,
  } = useContext(NotificationReportContext);

  const { navigateEntity } = useNavigateEntity();

  const channelPerformance = [
    {
      key: "overall",
      title: formatMessage({ id: "notification.report.overallPerformance" }),
      sent: {
        number: channelPerformanceData?.overall?.sent,
      },
      opened: {
        number: channelPerformanceData?.overall?.opened,
      },
      conversionRate: {
        number:
          channelPerformanceData?.overall?.opened /
          channelPerformanceData?.overall?.sent,
      },
    },
    {
      key: "widget",
      title: formatMessage({ id: "notification.report.widgetPerformance" }),
      icon: (
        <Box
          component="i"
          mr={"8px"}
          className="meta-crm-icon-ic_channel_widget font-size-16"
        />
      ),
      sent: {
        number: channelPerformanceData?.widget?.sent,
      },
      opened: {
        number: channelPerformanceData?.widget?.opened,
      },
      conversionRate: {
        number:
          channelPerformanceData?.widget?.opened /
          channelPerformanceData?.widget?.sent,
      },
      subscribedUsers: {
        title: formatMessage({ id: "global.metrics.impression" }),
        value: channelPerformanceData?.subscribedUsers?.widget || "-",
        tooltip: formatMessage({ id: "explanation.action.widgetImpression" }),
      },
    },
    {
      key: "discord",
      title: formatMessage({ id: "notification.report.discordPerformance" }),
      icon: (
        <Box
          component="i"
          mr={"8px"}
          className="meta-crm-icon-ic_channel_discord font-size-16"
        />
      ),
      sent: {
        number: channelPerformanceData?.discord?.sent,
      },
      opened: {
        number: null, // channelPerformanceData?.discord?.opened
      },
      conversionRate: {
        number: null,
        // channelPerformanceData?.discord?.opened /
        // channelPerformanceData?.discord?.sent,
      },
      subscribedUsers: {
        title: formatMessage({ id: "global.function.subscriberCount" }),
        value: channelPerformanceData?.subscribedUsers?.discord || "-",
        tooltip: formatMessage({ id: "explanation.action.subscriberCount" }),
      },
    },
    {
      key: "telegram",
      title: formatMessage({ id: "notification.report.telegramPerformance" }),
      icon: (
        <Box
          component="i"
          mr={"8px"}
          className="meta-crm-icon-ic_channel_telegram font-size-16"
        />
      ),
      sent: {
        number: channelPerformanceData?.telegram?.sent,
      },
      opened: {
        number: null, // channelPerformanceData?.telegram?.opened,
      },
      conversionRate: {
        number: null,
        // channelPerformanceData?.telegram?.opened /
        // channelPerformanceData?.telegram?.sent,
      },
      subscribedUsers: {
        title: formatMessage({ id: "global.function.subscriberCount" }),
        value: channelPerformanceData?.subscribedUsers?.telegram || "-",
      },
    },
    {
      key: "email",
      title: formatMessage({ id: "notification.report.emailPerformance" }),
      icon: (
        <Box
          component="i"
          mr={"8px"}
          className="meta-crm-icon-ic_channel_email font-size-16"
        />
      ),
      sent: {
        number: channelPerformanceData?.email?.sent,
      },
      opened: {
        number: channelPerformanceData?.email?.opened,
      },
      conversionRate: {
        number:
          channelPerformanceData?.email?.opened /
          channelPerformanceData?.email?.sent,
      },
      subscribedUsers: {
        title: formatMessage({ id: "global.function.subscriberCount" }),
        value: channelPerformanceData?.subscribedUsers?.email || "-",
      },
    },
    {
      key: "xmtp",
      title: formatMessage({ id: "notification.report.xmtpPerformance" }),
      icon: (
        <Box
          component="i"
          mr={"8px"}
          className="meta-crm-icon-ic_channel_xmtp font-size-16"
        />
      ),
      sent: {
        number: channelPerformanceData?.xmtp?.sent,
      },
      opened: {
        number: null, // channelPerformanceData?.xmtp?.opened,
      },
      conversionRate: {
        number: null,
        // channelPerformanceData?.xmtp?.opened /
        // channelPerformanceData?.xmtp?.sent,
      },
    },
  ];

  const headCellsData = [
    {
      show: true,
      disabledSort: false,
      id: "notificationTitle",
      label: (
        <StyledTableHeader sx={{ paddingLeft: "8px" }}>
          {formatMessage({ id: "notification.create.notificationTitle" })}
        </StyledTableHeader>
      ),
    },
    {
      show: true,
      disabledSort: false,
      id: "channel",
      label: (
        <StyledTableHeader>
          {formatMessage({ id: "notification.global.channel" })}
        </StyledTableHeader>
      ),
    },
    {
      show: true,
      disabledSort: false,
      id: "sendTime",
      label: (
        <StyledTableHeader>
          {formatMessage({ id: "notification.global.sendTime" })}
        </StyledTableHeader>
      ),
    },
    {
      show: true,
      disabledSort: false,
      id: "sent",
      label: (
        <StyledTableHeader>
          {formatMessage({ id: "global.metrics.sent" })}
        </StyledTableHeader>
      ),
    },
    {
      show: true,
      disabledSort: false,
      id: "opened",
      label: (
        <StyledTableHeader>
          {formatMessage({ id: "global.metrics.open" })}%
        </StyledTableHeader>
      ),
    },
    {
      show: true,
      disabledSort: true,
      id: "actions",
      maxWidth: 80,
      label: (
        <StyledTableHeader>
          {formatMessage({ id: "global.header.actions" })}
        </StyledTableHeader>
      ),
    },
  ];

  const renderFilter = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: "8px",
        }}
        className="fade-up-more"
      >
        <Box className="startRow" sx={{ flexWrap: "wrap", width: "100%" }}>
          <Box mr={1}>
            <InputField
              size={SIZE.MEDIUM}
              style={{ width: "360px" }}
              startAdornment={
                <i className="meta-crm-icon-ic_search font-size-14" />
              }
              placeholder={formatMessage({
                id: "notification.center.searchPlaceHolder",
              })}
              value={searchText}
              onChange={handleSearchChange}
            />
          </Box>

          <Box ml="auto">
            <DateArea
              sx={{
                marginBottom: "0px",
                marginTop: "0px",
                padding: "0px",
                backgroundColor: "transparent",
              }}
              context={NotificationReportContext}
              quickSelectOptions={[
                {
                  days: 1,
                  label: formatMessage({ id: "datePicker.range.day" }),
                },
                {
                  days: 7,
                  label: formatMessage({ id: "datePicker.range.week" }),
                },
                {
                  days: 30,
                  label: formatMessage({ id: "datePicker.range.month" }),
                },
                {
                  days: "custom",
                  label: formatMessage({ id: "datePicker.range.custom" }),
                },
              ]}
              datePickerProps={{
                minDate: null,
                maxDate: new Date(),
              }}
            ></DateArea>
          </Box>
        </Box>
      </Box>
    );
  };
  return (
    <div>
      {renderFilter()}
      <Box component={Grid} container spacing={"12px"} className="fade-up-more">
        {channelPerformance.map((item, index) => (
          <Box component={Grid} item xs={12} lg={6}>
            <ChannelPerformance
              channelPerformanceData={item}
              key={index}
            ></ChannelPerformance>
          </Box>
        ))}
      </Box>
      <Box mt={3} className="fade-up-more">
        <Box sx={{ color: "#000", marginBottom: "12px" }}>
          {formatMessage({
            id: "notification.report.detailedTitle",
          })}
        </Box>
        <CRMTable
          rowData={notificationList}
          headers={headCellsData}
          contents={map(notificationList, (dataItem, i) =>
            renderContents(dataItem)
          )}
          onRequestSort={handleRequestSort}
          order={order}
          orderBy={orderBy}
          isLoading={loading}
          loadingIndicatorAmount={4}
          onRowClick={(notificationId) => {
            navigateEntity(
              `/notification/notification-detail/${notificationId}`
            );
          }}
          dataCounts={totalItems}
          page={page}
          setPage={handleChangePage}
        />
      </Box>

      <PopperMenu
        placement="bottom-end"
        open={Boolean(anchorEl)}
        onClose={handleCloseActions}
        anchorEl={anchorEl}
        itemList={actionsList}
      />
    </div>
  );
};

export default NotificationReportContent;
